import { Observable, BehaviorSubject, of as observableOf } from 'rxjs';
import { mergeMap, catchError, share, filter, map } from "rxjs/operators";
import { addProvider, IProviderDescriptor } from "../ServiceUtil";
import { ProjectDelegate } from "./project.delegate";
import { IProject, INewProject, IProjectCodeRate, IProjectCodeStatus, IProjectCodeResponse, INewProjectContractDocument } from "./project.interface";
import { AuthenticationService } from "../AuthenticationService";
import { IEmployee, EmployeeService } from "../EmployeeService";

export class ProjectService
{
 

    private projectCodesData: IProject[] = null;
    private projectCodeSubject: BehaviorSubject<IProject[]> = new BehaviorSubject(null);
    public projectCodes: Observable<IProject[]> = this.projectCodeSubject;

    private newProjectCodesData: IProject[] = null;
    private newProjectCodeSubject: BehaviorSubject<IProject[]> = new BehaviorSubject(null);
    public newProjectCodes: Observable<IProject[]> = this.newProjectCodeSubject;

    private allProjectsSubject: BehaviorSubject<IProject[]> = new BehaviorSubject(null);
    public allProjects: Observable<IProject[]> = this.allProjectsSubject;

    private projectCodeFilteredSubject: BehaviorSubject<IProject[]> = new BehaviorSubject(null);
    public projectCodesFiltered: Observable<IProject[]> = this.projectCodeFilteredSubject;

    private selectedProjectCodeId: string = "0";
    public filterProjectsStatus: boolean = true;
    private filterProjectStatus$: BehaviorSubject<boolean> = new BehaviorSubject(true);

    private employeeListForProjectsData: IEmployee[] = null;
    private employeeListForProjectsSubject: BehaviorSubject<IEmployee[]> = new BehaviorSubject(null);
    public employeeListForProjects: Observable<IEmployee[]> = this.employeeListForProjectsSubject;


    private selectedProjectCodesData: IProject = null;
    private selectedProjectCodeSubject: BehaviorSubject<IProject> = new BehaviorSubject(this.selectedProjectCodesData);
    public selectedProjectCode: Observable<IProject> = this.selectedProjectCodeSubject;

    constructor(private projectDelegate: ProjectDelegate,
        private authenticationService: AuthenticationService,
        private employeeService: EmployeeService)
    {
        this.employeeService.allEmployees.pipe(filter(data => !!data), map((data: any) =>
        {
            this.employeeListForProjectsData = data;
            this.setEmployeesListForProjects(this.filterProjectsStatus);
        })).subscribe();
    }

    public get projectCodeId(): string
    {
        return this.selectedProjectCodeId;
    }

    public setSelectedProjectCodeId(projectCodeId: string): void
    {
        this.selectedProjectCodeId = projectCodeId;
    }

    getSelectedProjectCode()
    {
        return this.projectCodesData.find((projectItem: IProject) =>
            projectItem.projectCodeId === this.projectCodeId);
    }

    public getProjectCodeById(projectCodeId: string): Observable<boolean>
    {
        if (projectCodeId === "0")
        {
            this.selectedProjectCodesData = null;
            this.selectedProjectCodeSubject.next(this.selectedProjectCodesData);
            return observableOf(false);
        }
        const response = this.projectDelegate.getProjectCodeById(projectCodeId);
        return this.processProjectCode(response);
    }

    public getAllProjectCode(hardReload: boolean = false): Observable<boolean>
    {
        if (this.projectCodesData !== null && this.projectCodesData.length > 0 && hardReload === false)
        {
            return;
        }
        const orgId = this.authenticationService.GetOrgId();
        const response = this.projectDelegate.getAllProjectCodes(orgId);
        return this.processProjectCodes(response);
    }

    public getAllNewProjectCode(hardReload: boolean = false): Observable<boolean>
    {
        if (this.newProjectCodesData !== null && this.newProjectCodesData.length > 0 && hardReload === false)
        {
            return;
        }
        const orgId = this.authenticationService.GetOrgId();
        const response = this.projectDelegate.getAllNewProjectCodes(orgId);
        return this.processProjectCodes(response, false);
    }

    public createNewProject(projectCode: INewProject): Observable<boolean>
    {
        const response = this.projectDelegate.createNewProjectCode({
            ...projectCode,
            ...this.authenticationService.getBaseRequest
        });
        return this.processProjectCodes(response);
    }

    public createNewContractDocuments(documents: INewProjectContractDocument): Observable<boolean>
    {
        const response = this.projectDelegate.createProjectCideContracts({
            ...documents,
            ...this.authenticationService.getBaseRequest
        });
        return this.processProjectCode(response);
    }

    public updateProjectCode(project: IProject): Observable<boolean>
    {
        const response = this.projectDelegate.updateProjectCode({
            ...project,
            ...this.authenticationService.getBaseRequest
        });
        return this.processProjectCode(response);
    }

    public createProjectCodeRate(projectCodeRte: IProjectCodeRate): Observable<boolean>
    {
        const response = this.projectDelegate.createProjectCodeRate({
            ...projectCodeRte,
            ...this.authenticationService.getBaseRequest
        });
        return this.processProjectCode(response);
    }

    public updateProjectCodeRate(projectCodeRte: IProjectCodeRate): Observable<boolean>
    {
        const response = this.projectDelegate.updateProjectCodeRate({
            ...projectCodeRte,
            ...this.authenticationService.getBaseRequest
        });
        return this.processProjectCode(response);
    }

    public createProjectCodeStatus(projectCodeStatus: IProjectCodeStatus): Observable<boolean>
    {
        const response = this.projectDelegate.createProjectCodeStatus({
            ...projectCodeStatus,
            ...this.authenticationService.getBaseRequest
        });
        return this.processProjectCode(response);
    }

    public getProjectsByStatus(active: boolean)
    {
        this.filterProjectsStatus = active;
        let data = this.projectCodesData;
        data = this.projectCodesData.filter(x => x.status === active);
        this.projectCodeFilteredSubject.next(data);
        this.filterProjectStatus$.next(this.filterProjectsStatus);
        this.setEmployeesListForProjects(active);
    }

    public setProjectDataInList()
    {
        let data = this.projectCodesData;
        const index = this.projectCodesData.findIndex(x => x.projectCodeId === this.selectedProjectCodesData.projectCodeId);
        this.projectCodesData[index] = this.selectedProjectCodesData;
        data = this.projectCodesData.filter(x => x.status === this.filterProjectsStatus);

        this.projectCodeFilteredSubject.next(data);
        this.filterProjectStatus$.next(this.filterProjectsStatus);
        this.setEmployeesListForProjects(this.filterProjectsStatus);
    }

    public setEmployeesListForProjects(active: boolean)
    {
        if (!this.employeeListForProjectsData) { return; }
        const data = this.employeeListForProjectsData.filter(x => x.active === active);
        this.employeeListForProjectsSubject.next(data);
    }

    public filterProjects(searchTerm: string, filterBy: string)
    {
        let data = this.projectCodesData;
        if (filterBy == null)
        {
            data = this.projectCodesData.filter(x =>
                x.projectCode1.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
                || x.clientName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
                || x.vendorName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }
        else 
        {
            data = this.projectCodesData.filter(x =>
                x[filterBy].trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }

        this.projectCodeFilteredSubject.next(data);
    }

    private processProjectCodes(projectCodes: Observable<IProjectCodeResponse>, allProjectsDefault = true): Observable<boolean>
    {
        projectCodes.subscribe(onSuccess.bind(this), onFault.bind(this));

        return projectCodes.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(projectCodeResponse: IProjectCodeResponse)
        {
            if (projectCodeResponse.success && projectCodeResponse.projectCodes.length > 0)
            {
                this.selectedProjectCodeId = projectCodeResponse.projectCodeId ? projectCodeResponse.projectCodeId : this.selectedProjectCodeId;

                if (!allProjectsDefault)
                {
                    this.newProjectCodesData = projectCodeResponse.projectCodes;
                    this.newProjectCodeSubject.next(this.newProjectCodesData);
                    return;
                }
                this.projectCodesData = projectCodeResponse.projectCodes;
                this.projectCodeSubject.next(this.projectCodesData.filter(x => x.status === true));
                this.allProjectsSubject.next(this.projectCodesData);
                this.getProjectsByStatus(this.filterProjectsStatus);
            }
        }

        function onFault(error: any)
        {
            //  ProjectService.logger.error(`processImmigrationList Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processProjectCode(projectCodes: Observable<IProjectCodeResponse>): Observable<boolean>
    {
        projectCodes.subscribe(onSuccess.bind(this), onFault.bind(this));

        return projectCodes.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(projectCodeResponse: IProjectCodeResponse)
        {
            if (projectCodeResponse.success && projectCodeResponse.projectCode)
            {
                this.selectedProjectCodeId = projectCodeResponse.projectCodeId ? projectCodeResponse.projectCodeId : this.selectedProjectCodeId;

                this.selectedProjectCodesData = projectCodeResponse.projectCode;
                this.selectedProjectCodeSubject.next(this.selectedProjectCodesData);
                this.setProjectDataInList();
            }
        }

        function onFault(error: any)
        {
            //  ProjectService.logger.error(`processImmigrationList Fault - Error (${JSON.stringify(error)})`);
        }
    }
}